/**
 * Dutch (Netherlands) widget strings. Second shipped table after es_ES
 * (#1262); same shape, same gate, same resolution ladder. `nl_BE` and
 * `nl_NL_formal` visitors land here through the same-language fallback.
 *
 * PROVENANCE (toolkit parity, searchbar-pro 3.288.0 / CoreI18n):
 *   - Strings whose English matches a toolkit msgid are taken verbatim from
 *     the toolkit's own Dutch tables on origin/main
 *     (`includes/SearchbarPro/Translations.php` ROWS and
 *     `includes/CoreI18n/Translations.php` 'nl'); where only the placeholder
 *     syntax differs ({night} → {{n}}, "Loading…" → "Laden...") the wording
 *     is the toolkit's with the widget's tokens. Those keys:
 *       specs.{bedrooms,bathrooms,guests}; property-card/explorer
 *       feature_{guest,guests,bedroom,bedrooms}, price_per_night, price_total,
 *       price_avg_per_night, price_excl_taxes, price_excl_taxes_fees,
 *       price_night_*, grid_load_more, grid_loading, slider_next;
 *       calendar.{error_disabled_error, error_check_in_min_stay_error,
 *       tip_select_stay, tip_night_*, footer_clear_dates, min_stay_unit_*};
 *       booking.{button_check_availability, button_close, label_check_in*,
 *       label_check_out*, placeholder_add_date, label_guests, label_adults,
 *       label_children, label_infants, label_pets, hint_*_age,
 *       aria_guest_selection, aria_decrease_*, aria_increase_*, unit_*,
 *       label_min_stay, pill_min_stay*, label_total_after_tax,
 *       label_total_before_taxes, unit_per_night, unit_total,
 *       calendar_modal_title, label_when, button_clear_dates, label_subtotal,
 *       label_total_modal, group_fees, group_addons, group_others, group_taxes,
 *       notice_coupon_needs_code};
 *       checkout.{button_back, label_city, label_check_in, label_check_out,
 *       label_guests, label_nights, aria_close_consent_dialog, group_*};
 *       confirmation.{aria_modal_close, label_row_checkin, label_row_checkout,
 *       label_row_guests, group_*}.
 *   - EVERY OTHER STRING WAS WRITTEN HERE on 2026-10-18 (lane sbp-catchup)
 *     against the en_US schema defaults and has NOT been reviewed by a native
 *     speaker. Register: neutral/impersonal where the English allows it,
 *     informal "je" where a pronoun is unavoidable (the register Airbnb NL
 *     uses); the toolkit's CoreI18n table uses formal "uw" in a few places.
 *
 * REVIEW (choices a native reviewer may want to change):
 *   - property-card.feature_bedrooms_short "slpk." / feature_baths_short
 *     "badk." — overlay abbreviations; Dutch has no standard short form.
 *   - property-card.view_property "Bekijk accommodatie" — the toolkit's
 *     "Search %s home" uses "Bekijk %s accommodatie"; "woning" is what
 *     CoreI18n uses for "home".
 *   - checkout.label_city "Stad" — the toolkit's choice; Dutch address forms
 *     often say "Plaats".
 *   - explorer.price_avg_per_night "gem./nacht" — CoreI18n's "{price} avg / night".
 *   - *.feature_studio / specs.studio "Studio" and confirmation.label_row_status
 *     "Status" are the Dutch words (listed in IDENTICAL_BY_DESIGN).
 *
 * Placeholders ({{n}}, {{from}}, %1, …) are the same tokens the English
 * defaults carry; the integrity test pins them.
 */
import type { LocaleTable, WidgetStrings } from './tables'

const propertyCard: WidgetStrings<'property-card'> = {
  price_from: 'Vanaf',
  price_per_night: '/ nacht',
  view_property: 'Bekijk accommodatie',
  feature_guest: 'gast',
  feature_guests: 'gasten',
  feature_bedroom: 'slaapkamer',
  feature_bedrooms: 'slaapkamers',
  feature_bedrooms_short: 'slpk.',
  feature_studio: 'Studio',
  feature_bath: 'badkamer',
  feature_baths: 'badkamers',
  feature_baths_short: 'badk.',
  rating_reviews: 'beoordelingen',
  photo_previous: 'Vorige foto',
  photo_next: 'Volgende foto',
  photo_position: 'Foto %1 van %2',
}

const paymentForm = {
  heading_payment_information: 'Betaalgegevens',
  status_loading_payment_form: 'Betaalformulier wordt geladen...',
  label_name_on_card: 'Naam op de kaart',
  placeholder_name_on_card: 'Naam op de kaart',
  label_card_number: 'Kaartnummer',
  label_card_expiry: 'Vervaldatum',
  placeholder_card_expiry: 'MM/JJ',
  label_card_cvc: 'CVC',
  text_powered_by: 'Mogelijk gemaakt door',
  error_payment_processor_load_failed: 'De betaalprovider kon niet worden geladen',
  error_card_form_load_failed: 'Het kaartformulier kon niet worden geladen. Vernieuw de pagina en probeer het opnieuw.',
  error_payment_processor_not_ready: 'De betaalprovider is nog niet gereed',
  error_cvc_check_failed: 'De controle van de beveiligingscode (CVC) is mislukt. Controleer je kaartgegevens.',
  error_payment_processing_failed: 'De betaling kon niet worden verwerkt',
  error_payment_setup_intent: 'De kaartverificatie kon niet worden gestart',
  error_guestypay_load_failed: 'GuestyPay kon niet worden geladen',
  error_guestypay_unavailable: 'GuestyPay-tokenisatie is niet beschikbaar',
  error_payment_form_load_failed: 'Het betaalformulier kon niet worden geladen',
  error_payment_failed: 'Betaling mislukt',
  error_enter_card_details: 'Voer je kaartgegevens in',
} as const

const costGroups = {
  group_rent: 'Huur:',
  group_fees: 'Toeslag(en):',
  group_addons: 'Extra optie(s):',
  group_others: 'Overige:',
  group_taxes: 'Belasting(en):',
} as const

const outcomeCopy = {
  inquirySent: 'Aanvraag verzonden!',
  requestSent: 'Boekingsverzoek verzonden!',
  bookingConfirmed: 'Boeking bevestigd!',
  inquiryBody: 'Je aanvraag is naar de beheerder van de accommodatie verzonden. Je ontvangt binnenkort een reactie.',
  requestBody: 'Je boekingsverzoek is ingediend. De host beoordeelt het en bevestigt je reservering.',
  bookingBody: 'Je reservering is bevestigd. Je ontvangt binnenkort een bevestigingsmail.',
} as const

const offers = {
  weeklyAndMonthly: 'Bespaar {{weekly}}% op weekverblijven en {{monthly}}% op maandverblijven!',
  weekly: 'Bespaar {{weekly}}% op weekverblijven!',
  monthly: 'Bespaar {{monthly}}% op maandverblijven!',
} as const

export const nl_NL: LocaleTable = {
  amenities: {
    showMoreButton: 'Meer tonen',
  },

  description: {
    showMoreButton: 'Meer tonen',
  },

  specs: {
    bedrooms: 'Slaapkamers',
    bathrooms: 'Badkamers',
    guests: 'Gasten',
    areaSize: 'Oppervlakte',
    studio: 'Studio',
  },

  calendar: {
    error_disabled_error: 'Sorry, de gekozen datum is niet beschikbaar',
    error_check_in_min_stay_error: 'Uitchecken is op deze dag niet mogelijk: deze accommodatie vereist een minimaal verblijf van {{value}}',
    error_select_range_between_unavailable_days_error: 'Selecteer een periode tussen beschikbare dagen',
    error_advance_notice_error: 'Moet minimaal {{days}} dag(en) van tevoren worden geboekt',
    tip_select_stay: 'Kies {{from}} – {{to}} · {{nights}}',
    tip_night_one: '{{n}} nacht',
    tip_night_other: '{{n}} nachten',
    header_offer_weekly_and_monthly: offers.weeklyAndMonthly,
    header_offer_weekly: offers.weekly,
    header_offer_monthly: offers.monthly,
    header_local_time: 'Lokale tijd: ',
    footer_taxes_note: 'Exclusief belastingen en kosten',
    footer_legend_available: 'Beschikbaar voor aankomst/vertrek',
    footer_legend_booked: 'Geboekt',
    footer_legend_unavailable: 'Niet beschikbaar of beperkt, bijvoorbeeld door een minimaal verblijf',
    footer_clear_dates: 'Datums wissen',
    min_stay_unit_one: 'nacht',
    min_stay_unit_other: 'nachten',
  },

  'property-card': propertyCard,

  explorer: {
    ...propertyCard,
    price_starting_at: 'Vanaf',
    price_night_one: 'nacht',
    price_night_other: 'nachten',
    price_total: 'totaal',
    price_avg_per_night: 'gem./nacht',
    price_view_full: 'Volledige prijs bekijken',
    price_incl_taxes: 'incl. belastingen en kosten',
    price_excl_taxes: 'excl. belastingen',
    price_excl_taxes_fees: 'excl. belastingen en kosten',
    grid_no_results: 'Geen accommodaties gevonden.',
    grid_load_more: 'Meer laden',
    grid_loading: 'Laden...',
    slider_previous: 'Vorige',
    slider_next: 'Volgende',
    slider_go_to: 'Ga naar dia %1',
  },

  booking: {
    notice_select_dates: 'Selecteer datums',
    notice_select_guests: 'Selecteer gasten',
    notice_hosted_checkout_failed: 'De boekingspagina kon niet worden geopend. Probeer het opnieuw.',
    notice_generic_error: 'Er is een fout opgetreden',
    notice_coupon_failed: 'De kortingscode kon niet worden toegepast',
    notice_coupon_needs_dates: 'Selecteer eerst datums en gasten',
    notice_coupon_needs_code: 'Voer een kortingscode in',
    notice_coupon_applied: 'Kortingscode toegepast!',
    notice_coupon_invalid: 'Kortingscode niet geldig voor deze datums',
    button_processing: 'Bezig met verwerken...',
    button_check_availability: 'Beschikbaarheid controleren',
    button_close: 'Sluiten',
    button_coupon_apply: 'Toepassen',
    button_coupon_applying: 'Toepassen…',
    label_check_in: 'Aankomst',
    label_check_out: 'Vertrek',
    label_check_in_static: 'Aankomst',
    label_check_out_static: 'Vertrek',
    placeholder_add_date: 'Datum toevoegen',
    label_guests: 'Gasten',
    label_adults: 'Volwassenen',
    label_children: 'Kinderen',
    label_infants: "Baby's",
    label_pets: 'Huisdieren',
    hint_adults_age: 'Vanaf 13 jaar',
    hint_children_age: '2 – 12 jaar',
    hint_infants_age: 'Jonger dan 2 jaar',
    hint_pets_not_allowed: 'Niet toegestaan in deze accommodatie',
    aria_guest_selection: 'Gasten kiezen',
    aria_decrease_guests: 'Minder gasten',
    aria_increase_guests: 'Meer gasten',
    aria_decrease_adults: 'Minder volwassenen',
    aria_increase_adults: 'Meer volwassenen',
    aria_decrease_children: 'Minder kinderen',
    aria_increase_children: 'Meer kinderen',
    aria_decrease_infants: "Minder baby's",
    aria_increase_infants: "Meer baby's",
    aria_decrease_pets: 'Minder huisdieren',
    aria_increase_pets: 'Meer huisdieren',
    unit_guest_one: 'gast',
    unit_guest_other: 'gasten',
    unit_infant_one: 'baby',
    unit_infant_other: "baby's",
    unit_pet_one: 'huisdier',
    unit_pet_other: 'huisdieren',
    guest_max_note: 'Deze accommodatie biedt plaats aan maximaal {{n}} {{unit}}.',
    guest_max_note_with_infants: "Deze accommodatie biedt plaats aan maximaal {{n}} {{unit}}, baby's niet meegerekend.",
    unit_night_one: 'nacht',
    unit_night_other: 'nachten',
    label_min_stay: 'Minimale verblijfsduur: {{value}}',
    pill_min_stay: 'Min. {{n}} nachten',
    pill_min_stay_one: 'Min. 1 nacht',
    label_have_coupon: 'Ik heb een kortingscode',
    placeholder_coupon_code: 'Kortingscode',
    label_total_after_tax: 'Totaal inclusief belasting',
    label_total_before_taxes: 'Totaal exclusief belasting',
    note_taxes_at_checkout: 'Belastingen worden bij het afrekenen toegevoegd',
    discount_weekly_and_monthly: offers.weeklyAndMonthly,
    discount_weekly: offers.weekly,
    discount_monthly: offers.monthly,
    dialog_title: 'Boeking',
    label_starting_at: 'Vanaf ',
    unit_per_night: ' / nacht',
    unit_total: ' totaal',
    aria_loading_price: 'Prijs wordt geladen',
    calendar_modal_title: 'Datums kiezen',
    label_when: 'Wanneer',
    button_clear_dates: 'Datums wissen',
    label_subtotal: 'Subtotaal:',
    label_total_modal: 'Totaal:',
    aria_charge_explanation: 'Over deze kosten',
    aria_price_details_dialog: 'Prijsdetails',
    aria_close_price_details: 'Prijsdetails sluiten',
    ...costGroups,
  },

  checkout: {
    // Checkout shell
    button_back: '← Terug',
    status_loading_checkout: 'Afrekenen wordt geladen...',
    button_processing: 'Bezig met verwerken...',
    button_explore_properties: 'Accommodaties bekijken',

    // Errors & notices
    notice_no_booking_selected: 'Geen boeking geselecteerd. Begin op een accommodatiepagina om af te rekenen.',
    error_checkout_load_failed: 'De afrekengegevens konden niet worden geladen.',
    error_checkout_data_unavailable: 'De afrekengegevens kunnen niet worden geladen.',
    error_load_payment_settings: 'De betaalinstellingen konden niet worden opgehaald',
    error_load_quote: 'De prijsopgave kon niet worden opgehaald',
    error_load_property: 'De accommodatie kon niet worden opgehaald',
    error_reservation_failed: 'De reservering kon niet worden aangemaakt. Probeer het opnieuw.',
    error_create_reservation: 'Reservering aanmaken mislukt',
    error_hosted_checkout_failed: 'Het externe afrekenen kon niet worden gestart. Probeer het opnieuw.',
    error_hosted_checkout_url: 'De afreken-URL kon niet worden opgehaald',
    error_addon_update_failed: 'Die extra optie kon niet worden bijgewerkt.',
    error_update_addon: 'Extra optie bijwerken mislukt',
    error_payment_session_not_resumed: 'Je betaalsessie kon niet worden hervat. Voer je gegevens opnieuw in om de boeking af te ronden.',
    error_threeds_failed: '3D Secure-verificatie mislukt. Probeer het opnieuw.',
    error_payment_session_wrong_booking: 'Je betaalsessie hoorde bij een andere boeking en kon niet worden hervat. Voer je betaalgegevens opnieuw in.',

    // Hosted checkout
    heading_complete_your_booking: 'Rond je boeking af',
    text_hosted_checkout_intro: 'Deze accommodatie gebruikt de beveiligde betaalomgeving van {{pms}}. Klik hieronder om door te gaan en je gegevens en betaling af te ronden.',
    label_pms_fallback_host: 'je host',
    button_continue_to_pms_checkout: 'Doorgaan naar afrekenen bij {{pms}}',
    status_redirecting: 'Doorsturen...',

    // Guest information
    heading_guest_information: 'Gastgegevens',
    label_first_name: 'Voornaam',
    placeholder_first_name: 'Voornaam',
    label_last_name: 'Achternaam',
    placeholder_last_name: 'Achternaam',
    label_email: 'E-mailadres',
    placeholder_email: 'naam@voorbeeld.nl',
    label_phone: 'Telefoon',
    placeholder_phone: 'Telefoonnummer',
    error_invalid_phone: 'Voer een geldig telefoonnummer in.',
    placeholder_survey_select: '— Kies —',
    placeholder_survey_other: 'Licht toe...',
    hint_all_fields_required: 'Alle velden zijn verplicht',
    heading_mailing_address: 'Postadres',
    label_address_line_1: 'Adresregel 1',
    placeholder_address_line_1: 'Straat en huisnummer',
    label_address_line_2: 'Adresregel 2',
    placeholder_address_line_2: 'Appartement, verdieping, enz.',
    label_city: 'Stad',
    label_state: 'Provincie',
    placeholder_state: 'Staat / Provincie',
    label_zip_code: 'Postcode',
    placeholder_zip_code: 'Postcode',
    label_country: 'Land',
    placeholder_select_country: 'Kies een land',

    // Agreements & consent
    heading_rental_agreement: 'Huurovereenkomst',
    text_acceptance_privacy_and_terms: 'Ik heb de bovenstaande huisregels en annuleringsvoorwaarden gelezen en ga ermee akkoord, evenals met het {{privacy}} en de {{terms}}.',
    text_acceptance_terms_only: 'Ik heb de bovenstaande huisregels en annuleringsvoorwaarden gelezen en ga ermee akkoord, evenals met de {{terms}}.',
    text_acceptance_privacy_only: 'Ik heb de bovenstaande huisregels en annuleringsvoorwaarden gelezen en ga ermee akkoord, evenals met het {{privacy}}.',
    text_acceptance_no_links: 'Ik heb de bovenstaande huisregels en annuleringsvoorwaarden gelezen en ga ermee akkoord.',
    label_privacy_policy: 'Privacybeleid',
    label_terms: 'Voorwaarden',
    label_signature_draw: 'Onderteken hieronder',
    label_signature_name: 'Onderteken door je volledige naam te typen',
    label_signature_initials: 'Typ je initialen om te ondertekenen',
    button_clear_signature: 'Handtekening wissen',
    aria_close_consent_dialog: 'Sluiten',

    // Payment form
    ...paymentForm,

    // Booking summary
    heading_booking_summary: 'Overzicht van je boeking',
    label_check_in: 'Aankomst',
    label_check_out: 'Vertrek',
    label_guests: 'Gasten',
    label_nights: 'Nachten',
    label_total: 'Totaal',
    ...costGroups,
    label_taxes: 'Belastingen',

    // Property & policies
    heading_house_rules: 'Huisregels',
    heading_cancellation_policy: 'Annuleringsvoorwaarden',
    heading_payment_schedule: 'Betaalschema',
    text_payment_term_at_booking: 'bij boeking',
    label_payment_term_before: 'voor',
    label_payment_term_after: 'na',
    text_payment_term_timing_relative: '{{count}} {{unit}} {{relation}} {{event}}',
    text_payment_term_line: '{{amount}} {{timing}}',

    // Confirmation screen
    heading_inquiry_sent: outcomeCopy.inquirySent,
    heading_request_sent: outcomeCopy.requestSent,
    heading_booking_confirmed: outcomeCopy.bookingConfirmed,
    text_inquiry_sent_body: outcomeCopy.inquiryBody,
    text_request_sent_body: outcomeCopy.requestBody,
    text_booking_confirmed_body: outcomeCopy.bookingBody,
    label_reservation_id: 'Reserveringsnummer:',
    notice_payment_required: 'Voor je reservering is een betaling vereist. Rond de betaling af om je boeking te bevestigen.',
    button_complete_payment: 'Betaling afronden',
    button_go_back: 'Terug',
  },

  confirmation: {
    // Page states
    status_loading_reservation: 'Reserveringsgegevens worden geladen...',
    error_invalid_link: 'Sorry, de link die je hebt gevolgd is ongeldig of verlopen.',
    error_reservation_unavailable: 'De reserveringsgegevens kunnen niet worden geladen.',
    error_fetch_reservation_failed: 'De reservering kon niet worden opgehaald',

    // Success modal
    heading_modal_inquiry_sent: outcomeCopy.inquirySent,
    heading_modal_request_sent: outcomeCopy.requestSent,
    heading_modal_booking_confirmed: outcomeCopy.bookingConfirmed,
    text_modal_message_inquiry: outcomeCopy.inquiryBody,
    text_modal_message_request: outcomeCopy.requestBody,
    text_modal_message_booking: outcomeCopy.bookingBody,
    aria_modal_close: 'Sluiten',
    button_modal_got_it: 'Begrepen!',

    // Reservation summary
    label_row_type: 'Soort',
    label_row_status: 'Status',
    label_row_checkin: 'Aankomst',
    label_row_checkout: 'Vertrek',
    label_row_guests: 'Gasten',
    label_type_inquiry: 'Aanvraag',
    label_type_booking: 'Boeking',
    label_type_request: 'Boekingsverzoek',
    label_status_received: 'Ontvangen',
    label_status_pending: 'In behandeling',
    label_status_confirmed: 'Bevestigd',
    label_status_booked: 'Geboekt',
    label_status_requested: 'Aangevraagd',
    label_status_closed: 'Gesloten',

    // Cost breakdown
    heading_cost_breakdown: 'Kostenoverzicht',
    label_total: 'Totaal',
    label_taxes: 'Belastingen',
    ...costGroups,

    // Payment recovery
    notice_payment_method_failed: 'Je vorige betaalmethode is mislukt. Voer nieuwe betaalgegevens in.',
    button_pay_now: 'Nu betalen',
    status_completing_payment: 'Je betaling wordt afgerond…',
    button_submitting: 'Verzenden…',
    button_submit_payment: 'Betaling verzenden',
    text_payment_recovery_success: 'Bedankt, je betaalgegevens zijn verzonden.',
    error_payment_submit_failed: 'De betaling kon niet worden verzonden. Probeer het opnieuw.',
    error_threeds_auth_failed: '3D Secure-verificatie mislukt. Probeer het opnieuw.',
    error_threeds_resume_failed: 'Je betaalsessie kon niet worden hervat. Voer je betaalgegevens opnieuw in.',
    error_threeds_recovery_unavailable: 'Je kaart is geverifieerd, maar de betaling kon op deze pagina niet worden afgerond. Neem contact met ons op om de betaling af te ronden.',

    // Payment form (shared with Checkout)
    ...paymentForm,
  },
}
